import type {FileTreeSelectNode} from '../components/FileTreeSelect'

import type {RepoFileTree} from './fetch-repo-files'

interface FlatNode {
  name: string
  path: string
  kind: 'file' | 'directory'
}

// A Map (not a plain object) keeps directory names like '__proto__'/'constructor' from colliding with Object.prototype.
function buildChildrenByDir({paths, directories}: RepoFileTree): Map<string, FlatNode[]> {
  const childrenByDir = new Map<string, FlatNode[]>()
  const childrenOf = (dir: string): FlatNode[] => {
    let children = childrenByDir.get(dir)
    if (!children) {
      children = []
      childrenByDir.set(dir, children)
    }
    return children
  }

  childrenOf('')
  const directorySet = new Set(directories)

  for (const path of [...directories].sort()) {
    const parent = path.substring(0, path.lastIndexOf('/')) || ''
    const name = path.substring(path.lastIndexOf('/') + 1)
    childrenOf(parent).push({name, path, kind: 'directory'})
    childrenOf(path)
  }

  for (const path of paths.filter(p => !directorySet.has(p)).sort()) {
    const parent = path.substring(0, path.lastIndexOf('/')) || ''
    const name = path.substring(path.lastIndexOf('/') + 1)
    childrenOf(parent).push({name, path, kind: 'file'})
  }

  return childrenByDir
}

function toNodes(childrenByDir: Map<string, FlatNode[]>, dir: string): FileTreeSelectNode[] {
  const children = childrenByDir.get(dir) ?? []
  return children
    .map(({name, path, kind}) => ({
      id: path,
      name,
      kind,
      children: kind === 'directory' ? toNodes(childrenByDir, path) : undefined,
    }))
    .sort((a, b) => {
      if (a.kind === 'directory' && b.kind !== 'directory') return -1
      if (a.kind !== 'directory' && b.kind === 'directory') return 1
      return a.name.localeCompare(b.name)
    })
}

export function buildFileTreeSelectNodes(fileTree: RepoFileTree): FileTreeSelectNode[] {
  return toNodes(buildChildrenByDir(fileTree), '')
}
